'use client';

import React from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from './locales';
import {
    QueryClientProvider,
    QueryClient,
    isServer,
} from '@tanstack/react-query';
import { SharedProvider, ToastProvider } from '@get-mosh/msh-shared';
import { AppProvider } from 'src/context/app';
import DatadogInit from 'src/analytics/Datadog';
import { env } from 'src/env.mjs';
import { GoogleOAuthProvider } from '@react-oauth/google';
import AnalyticsProvider from 'src/analytics/Analytics';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/next/app';

type ProvidersProps = {
    children: React.ReactNode;
};

function makeQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // With SSR, we usually want to set some default staleTime
                // above 0 to avoid refetching immediately on the client
                staleTime: 60 * 1000,
            },
        },
    });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
    if (isServer) {
        // Server: always make a new query client
        return makeQueryClient();
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) browserQueryClient = makeQueryClient();
        return browserQueryClient;
    }
}

function Providers({ children }: ProvidersProps) {
    const client = getQueryClient();

    return (
        <I18nextProvider i18n={i18n}>
            <AppProvider>
                <QueryClientProvider client={client}>
                    <AnalyticsProvider>
                        <GoogleOAuthProvider
                            clientId={env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}
                        >
                            <SharedProvider theme={env.NEXT_PUBLIC_BRAND}>
                                <ToastProvider>
                                    <NuqsAdapter>
                                        <DatadogInit />
                                        <ReactQueryDevtools
                                            initialIsOpen={false}
                                        />
                                        {children}
                                    </NuqsAdapter>
                                </ToastProvider>
                            </SharedProvider>
                        </GoogleOAuthProvider>
                    </AnalyticsProvider>
                </QueryClientProvider>
            </AppProvider>
        </I18nextProvider>
    );
}

export default Providers;
